import { TRACKING_SERVICE_URL } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { mixpanelService } from '@/services/mixpanel.service';
import type { MembershipTier } from '@/types/live-session';
import { useCallback, useEffect, useRef } from 'react'; 
const TRACKING_INTERVAL_SECONDS = 15; 

// Interface-t e eksportuara
export interface UserMetadata {
    wp_user_id?: string;
    membership_plan?: string;
    [key: string]: any;
}

export interface User { 
    id: string;
    user_metadata: UserMetadata;
    [key: string]: any;
}

function mapTierToProducts(tier: MembershipTier, fallback: string): string {
    if (tier === 'pro') return 'open-studio-pro';
    if (tier === 'regular') return 'open-studio';
    return fallback;
}

interface TrackingState {
    isPlaying: boolean;
    startPlayhead: number;
    previousPlayhead: number;
    loopWasUsed: boolean;
    loopIsActive: boolean;
    currentSpeed: number;
    recordingId: number | string;
    startSystemTime: number | null;
}

const initialTrackingState: TrackingState = {
    isPlaying: false,
    startPlayhead: 0,
    previousPlayhead: -1000,
    loopWasUsed: false,
    loopIsActive: false,
    currentSpeed: 1,
    recordingId: -1,
    startSystemTime: null,
};


/**
 * Get distinct_id for tracking. Falls back to user email if Mixpanel not ready.
 */
const getDistinctId = (userEmail?: string | null): string | null => {
    const distinctId = mixpanelService.getDistinctId();
    if (distinctId && typeof distinctId === 'string') {
        return distinctId;
    }
    return userEmail || null;
};

export function useSoundsliceTracker(
  isWebPlatform: boolean,
  isPlayerReady: boolean,
  lessonIdNumber: number | null, 
  currentTime: number, 
  videoDuration: number, 
  user: User | null, 
  courseSlug: string,
  lessonSlug: string,
  membershipTier: MembershipTier = 'non_member',
  videoId?: number | null,
  videoSlug?: string | null,
) {
    const timerRef = useRef<number | null>(null);
    const trackingStateRef = useRef<TrackingState>(initialTrackingState);
    const currentTimeRef = useRef<number>(currentTime);

    useEffect(() => {
        currentTimeRef.current = currentTime;
    }, [currentTime]);

    const getRoundedDuration = useCallback(() => Math.round(videoDuration), [videoDuration]);

    const sendTrackingEvent = useCallback(() => {
        const state = trackingStateRef.current;
        const distinct_id = getDistinctId(user?.email);
        if (!distinct_id) {
            logger.debug("Skipping tracking payload: Could not retrieve valid distinct_id.");
            return; 
        }

        if (!state.startSystemTime || !user?.id || !lessonIdNumber) return;

        const newSystemTime = Date.now();
        const timeSinceStart = (newSystemTime - state.startSystemTime) / 1000;
        const secondsWatched = Math.min(TRACKING_INTERVAL_SECONDS, timeSinceStart);
        
        if (secondsWatched < 1 || Math.abs(state.startPlayhead - state.previousPlayhead) < 1) return;
        state.previousPlayhead = state.startPlayhead;
        
        const products = mapTierToProducts(membershipTier, 'none');

        // Build URL path for tracking (web vs native)
        const urlPath = isWebPlatform && typeof window !== 'undefined' 
            ? window.location.pathname 
            : `/courses/${courseSlug}/${lessonSlug}`;

        const payload = JSON.stringify({
            distinct_id: distinct_id, 
            url: urlPath, 
            postId: lessonIdNumber,
            duration: getRoundedDuration(),
            startPlayhead: state.startPlayhead,
            loopUsed: state.loopWasUsed,
            currentSpeed: state.currentSpeed,
            recordingId: String(state.recordingId),
            secondsWatched: secondsWatched,
            products,
            ...(videoId != null && videoId !== 0 && { video_id: videoId }),
            ...(videoSlug != null && videoSlug !== '' && { video_slug: videoSlug }),
        });
        
        // Send tracking event asynchronously to avoid blocking
        (async () => {
            try {
                const response = await fetch(TRACKING_SERVICE_URL, {
                    method: "POST",
                    body: payload,
                    headers: {
                        'Content-Type': 'application/json',
                        'Referer': isWebPlatform && typeof window !== 'undefined' 
                            ? window.location.origin 
                            : 'mobile-app', 
                    },
                });
                logger.debug(`Tracking service returned: ${response.status}`); 
            } catch (err) {
                logger.error("Tracking service error:", err);
            }
        })();

        state.startSystemTime = newSystemTime;
        
    }, [lessonIdNumber, getRoundedDuration, user, isWebPlatform, courseSlug, lessonSlug, videoId, videoSlug]);

    const handlePlayerStateChange = useCallback((isCurrentlyPlaying: boolean, newPlayhead?: number) => {
        const state = trackingStateRef.current;
        const finalPlayhead = newPlayhead !== undefined ? newPlayhead : currentTimeRef.current; 
        
        if (isCurrentlyPlaying && !state.isPlaying) {
            trackingStateRef.current = {
                ...state,
                isPlaying: true,
                startSystemTime: Date.now(),
                startPlayhead: finalPlayhead,
            };

            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = setInterval(sendTrackingEvent, TRACKING_INTERVAL_SECONDS * 1000) as unknown as number; 
            
        } else if (!isCurrentlyPlaying && state.isPlaying) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            
            sendTrackingEvent();

            trackingStateRef.current.isPlaying = false;
        }

    }, [sendTrackingEvent]);

    useEffect(() => {
        if (!isPlayerReady || !lessonIdNumber) return;

        const handleWebMessage = (event: MessageEvent) => {
            if (event.origin !== 'https://www.soundslice.com') return;

            try {
                let data: any;
                if (typeof event.data === "string") {
                    try {
                        data = JSON.parse(event.data);
                    } catch (parseError) {
                        // Silently ignore parsing errors - Soundslice sometimes sends non-JSON data
                        return;
                    }
                } else if (event.data && typeof event.data === "object") {
                    data = event.data;
                } else {
                    return;
                }
                
                if (!data || typeof data !== "object" || !data.method) return;

                switch (data.method) {
                    case 'ssPlay':
                        handlePlayerStateChange(true, data.arg);
                        break;
                    case 'ssPause':
                        handlePlayerStateChange(false);
                        break;
                    case 'ssSeek':
                        trackingStateRef.current.startPlayhead = data.arg;
                        break;
                    case 'ssCurrentTime':
                        trackingStateRef.current.startPlayhead = data.arg;
                        break;
                    case 'ssLoopChange':
                        trackingStateRef.current = {
                            ...trackingStateRef.current,
                            loopIsActive: (data.arg[0] > 0),
                            loopWasUsed: (data.arg[0] > 0) ? true : trackingStateRef.current.loopWasUsed,
                        };
                        break;
                    case 'ssAudioSourceChanged':
                        trackingStateRef.current.recordingId = data.arg;
                        break;
                    case 'ssSpeed':
                        trackingStateRef.current.currentSpeed = data.arg;
                        break;
                }
            } catch (e) {
                logger.debug("Error processing Soundslice message in tracker:", e);
            }
        };

        if (isWebPlatform) {
            window.addEventListener('message', handleWebMessage);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (isWebPlatform) {
                window.removeEventListener('message', handleWebMessage);
            }
        };
    }, [isPlayerReady, isWebPlatform, lessonIdNumber, lessonSlug, handlePlayerStateChange]);

    /**
     * Handle native WebView tracking events
     * Called from onMessage handler in VideoPlayerScreen
     */
    const handleNativeTrackingEvent = useCallback((eventType: string, data?: any) => {
        switch (eventType) {
            case 'TRACKING_PLAY':
                handlePlayerStateChange(true, data?.playhead);
                break;
            case 'TRACKING_PAUSE':
                handlePlayerStateChange(false);
                break;
            case 'TRACKING_SEEK':
                if (data?.playhead !== undefined) {
                    trackingStateRef.current.startPlayhead = data.playhead;
                }
                break;
            case 'TRACKING_LOOP':
                if (data?.loopInfo && Array.isArray(data.loopInfo)) {
                    trackingStateRef.current = {
                        ...trackingStateRef.current,
                        loopIsActive: (data.loopInfo[0] > 0),
                        loopWasUsed: (data.loopInfo[0] > 0) ? true : trackingStateRef.current.loopWasUsed,
                    };
                }
                break;
            case 'TRACKING_AUDIO_SOURCE':
                if (data?.recordingId !== undefined) {
                    trackingStateRef.current.recordingId = data.recordingId;
                }
                break;
            case 'TRACKING_SPEED':
                if (data?.speed !== undefined) {
                    trackingStateRef.current.currentSpeed = data.speed;
                }
                break;
        }
    }, [handlePlayerStateChange]);

    return {
        handlePlayerStateChange,
        handleNativeTrackingEvent,
    };
}